import { DeleteTwoTone, EditOutlined, MoreOutlined } from "@ant-design/icons";
import { Affix, Button, Card, Dropdown, Menu, Modal, Popconfirm, Typography } from "antd";
import ListTable from "../../../common/ListTable";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  BOOKING_QTY_DELETE_ENDPOINT,
  BOOKING_QTY_LIST_ENDPOINT,
  BOOKING_QTY_VIEW_ENDPOINT,
} from "../../../apiServices/API_ENDPOINTS";
import { deleteData, getData } from "../../../apiServices/common";
import { alertPop } from "../../../apiServices/common/helper";
import {
  BOOKING_QTY_CREATE_PERMISSION,
  BOOKING_QTY_DELETE_PERMISSION,
  BOOKING_QTY_EDIT_PERMISSION,
} from "../../../routes/permissions";
import { cglFormatDate, commaSeparateNumber, hasPermission } from "../../../utils/functions";
import { isMobileView } from "../../Common/AppHelper";
import CustomFilter from "../../Common/CustomFilter";
import Header from "../../Common/Header";
import BookingQtyForm from "./BookingQtyForm";

const BookingQty = (props) => {
  const { width } = props;
  const location = useLocation();
  const routerPath = location?.pathname || null;

  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [bookingList, setBookingList] = useState(null);
  const [totalData, setTotalData] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [rangeData, setRangeData] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [fetchError, setFetchError] = useState(null);
  const formRef = useRef(null);
  const lastFilterRef = useRef({});

  const { Title } = Typography;

  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};
    if (showFilter) {
      parsedFilterStatus[routerPath] = true;
    } else {
      parsedFilterStatus[routerPath] = false;
    }
    localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
  }, [showFilter, routerPath, location]);

  const getAllBookingQty = useCallback(
    async (passedObject) => {
      setLoading(true);
      setFetchError(null);
      if (passedObject && Object.keys(passedObject).length > 0) {
        lastFilterRef.current = passedObject;
      }
      const filter = {
        page: lastFilterRef.current?.page ?? currentPage,
        per_page: lastFilterRef.current?.per_page ?? 10,
        ...(lastFilterRef.current?.buyer_id && { buyer_id: lastFilterRef.current.buyer_id }),
      };
      if (filter.page) setCurrentPage(filter.page);
      if (filter.per_page) setRangeData(filter.per_page);
      const params = new URLSearchParams(filter);
      const response = await getData(`${BOOKING_QTY_LIST_ENDPOINT}?${params}`);
      if (response?.data?.code === 200) {
        setBookingList(response?.data?.data?.data || []);
        setTotalData(response?.data?.data?.total || 0);
        setRangeData(response?.data?.data?.per_page || 10);
      } else {
        alertPop("error", response?.data?.message || "Something Went Wrong!");
        setFetchError("Could not load this list.");
      }
      setLoading(false);
    },
    [currentPage],
  );

  const paginate = (page, pageSize) => {
    setCurrentPage(page);
    if (pageSize) setRangeData(pageSize);
    getAllBookingQty({ ...lastFilterRef.current, page, per_page: pageSize || rangeData });
  };

  useEffect(() => {
    getAllBookingQty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const removeBookingQty = async (id) => {
    const response = await deleteData(BOOKING_QTY_DELETE_ENDPOINT(id));
    if (response?.code === 200) {
      alertPop("success", "Deleted Successfully!");
      getAllBookingQty(lastFilterRef.current || {});
    } else {
      alertPop("error", response?.data?.message || "Something Went Wrong!");
    }
  };

  const openModalForEdit = async (record) => {
    setLoading(true);
    const response = await getData(BOOKING_QTY_VIEW_ENDPOINT(record.id));
    if (response?.data?.code === 200) {
      let item = response?.data?.data || {};
      // Fetch buyer label if not in response
      if (item.buyer_id && !item.buyer?.name) {
        try {
          const r = await getData(`/api/buyers/${item.buyer_id}/edit`);
          if (r?.data?.code === 200 && r?.data?.data?.name) {
            item = { ...item, buyer: { id: item.buyer_id, name: r.data.data.name } };
          } else {
            item = { ...item, buyer: { id: item.buyer_id, name: String(item.buyer_id) } };
          }
        } catch (_) {
          item = { ...item, buyer: { id: item.buyer_id, name: String(item.buyer_id) } };
        }
      } else if (item.buyer_id && item.buyer) {
        item = { ...item, buyer: { id: item.buyer_id, name: item.buyer.name || String(item.buyer_id) } };
      }
      // Fetch buying_office label if not in response
      if (item.buying_office_id && !item.buying_office?.name) {
        try {
          const r = await getData(`/api/buying_office/${item.buying_office_id}/edit`);
          if (r?.data?.code === 200 && r?.data?.data?.name) {
            item = { ...item, buying_office: { id: item.buying_office_id, name: r.data.data.name } };
          } else {
            item = { ...item, buying_office: { id: item.buying_office_id, name: String(item.buying_office_id) } };
          }
        } catch (_) {
          item = { ...item, buying_office: { id: item.buying_office_id, name: String(item.buying_office_id) } };
        }
      } else if (item.buying_office_id && item.buying_office) {
        item = {
          ...item,
          buying_office: { id: item.buying_office_id, name: item.buying_office.name || String(item.buying_office_id) },
        };
      }
      setSelectedItem(item);
      setIsEdit(true);
      setIsModalOpen(true);
    } else {
      alertPop("error", "Failed to load data");
    }
    setLoading(false);
  };

  const onModalOpen = () => {
    setSelectedItem(null);
    setIsEdit(false);
    setIsModalOpen(true);
  };

  const onCloseModal = () => {
    formRef.current?.resetFields?.();
    setIsModalOpen(false);
    setIsEdit(false);
    setSelectedItem(null);
  };

  const handleFilter = () => {
    setShowFilter((prev) => !prev);
  };

  const columns = [
    {
      title: "Buyer",
      dataIndex: ["buyer", "name"],
      key: "buyer",
    },
    {
      title: "Buying Office",
      dataIndex: ["buying_office", "name"],
      key: "buying_office",
    },
    {
      title: "Order Qty",
      dataIndex: "order_qty",
      key: "order_qty",
      render: (val) => commaSeparateNumber(val),
    },
    {
      title: "Production Month",
      dataIndex: "production_month",
      key: "production_month",
    },
    {
      title: "Request Date",
      dataIndex: "request_date",
      key: "request_date",
      render: (val) => cglFormatDate(val),
    },
    {
      title: "Confirm Date",
      dataIndex: "confirm_date",
      key: "confirm_date",
      render: (val) => cglFormatDate(val),
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      render: (_, record) => (
        <Dropdown
          overlay={
            <Menu
              items={[
                {
                  label: (
                    <span onClick={() => openModalForEdit(record)}>
                      <EditOutlined /> Edit
                    </span>
                  ),
                  key: "edit",
                  disabled: !hasPermission([BOOKING_QTY_EDIT_PERMISSION]),
                },
                {
                  label: (
                    <Popconfirm title="Are you sure you want to delete?" onConfirm={() => removeBookingQty(record.id)}>
                      <span>
                        <DeleteTwoTone twoToneColor="#ff4d4f" /> Delete
                      </span>
                    </Popconfirm>
                  ),
                  key: "delete",
                  disabled: !hasPermission([BOOKING_QTY_DELETE_PERMISSION]),
                },
              ]}
            />
          }
        >
          <MoreOutlined style={{ fontSize: "20px", cursor: "pointer" }} />
        </Dropdown>
      ),
    },
  ];

  const memoizedCustomFilter = useMemo(
    () => (
      <CustomFilter
        showFilter={showFilter}
        // setShowFilter={setShowFilter}
        // handleFilter={handleFilter}
        tableData={bookingList}
        fetchTableData={getAllBookingQty}
        filterName="booking_qty"
      />
    ),
    [showFilter],
  );

  return (
    <Fragment>
      <Affix offsetTop={0}>
        <Card className="p-fixed" style={{ width: `${isMobileView() ? "100%" : `${width}px`}` }}>
          <Header
            title={<Title level={5}>Booking QTY</Title>}
            actions={[
              <Button
                key="filter"
                type={showFilter ? "danger" : "primary"}
                size="small"
                onClick={handleFilter}
                style={{ float: "right" }}
              >
                {`${showFilter ? "Close" : "Open"} Filter Option`}
              </Button>,
              <Button
                key="add"
                type="primary"
                size="small"
                onClick={onModalOpen}
                disabled={!hasPermission([BOOKING_QTY_CREATE_PERMISSION])}
              >
                Add New
              </Button>,
            ]}
          />
        </Card>
      </Affix>

      <div className="containt-body">
        <Card>
          {memoizedCustomFilter}
          <ListTable
            loading={loading}
            error={fetchError}
            onRetry={() => getAllBookingQty(lastFilterRef.current || {})}
            emptyText="No records to show yet."
            dataSource={bookingList}
            columns={columns}
            bordered
            rowKey="id"
            pagination={{
              current: currentPage,
              total: totalData,
              pageSize: rangeData,
              onChange: (page, pageSize) => paginate(page, pageSize),
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            }}
          />
        </Card>
      </div>

      <Modal
        open={isModalOpen}
        title={isEdit ? "Edit Booking QTY" : "Add Booking QTY"}
        onCancel={onCloseModal}
        footer={null}
        maskStyle={{ backdropFilter: "blur(4px)" }}
        width={600}
      >
        <BookingQtyForm
          ref={formRef}
          isEdit={isEdit}
          onCloseModal={onCloseModal}
          refetch={() => getAllBookingQty(lastFilterRef.current || {})}
          selectedItem={selectedItem}
        />
      </Modal>
    </Fragment>
  );
};

export default BookingQty;
